// Login.jsx
import React, { useState } from "react";
import { ReactComponent as AuthLogoIcon } from "../../icons/auth-logo.svg";
import PrimaryButton from "../ui/buttons/PrimaryButton";
import SecondaryButton from "../ui/buttons/SecondaryButton";
import TextButton from "../ui/buttons/TextButton";
import AuthOverlay from "./AuthOverlay";
import { useDispatch } from "react-redux";
import { LoadingOutlined } from "@ant-design/icons";
import { loginUser } from "../../store/authSlice";

export default function Login({
  onClose,
  onSwitch,
  onForgot,
  loginUrl = "/user/login/",
}) {
  const [form, setForm] = useState({
    login: "",
    password: "",
  });
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({
    login: "",
    password: "",
    general: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const nextErrors = {
      login: "",
      password: "",
      general: "",
    };

    if (!form.login.trim()) {
      nextErrors.login = "Укажите логин";
    }
    if (!form.password.trim()) {
      nextErrors.password = "Укажите пароль";
    }

    if (nextErrors.login || nextErrors.password) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({ login: "", password: "", general: "" });

    try {
      await dispatch(
        loginUser({
          email: form.login,
          password: form.password,
        })
      ).unwrap();

      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      const data = err || {};
      const fieldErrors = { login: "", password: "", general: "" };

      if (data.email) {
        fieldErrors.login = Array.isArray(data.email)
          ? data.email.join(" ")
          : String(data.email);
      }
      if (data.password) {
        fieldErrors.password = Array.isArray(data.password)
          ? data.password.join(" ")
          : String(data.password);
      }

      const nonField =
        (Array.isArray(data.non_field_errors)
          ? data.non_field_errors.join(" ")
          : data.non_field_errors) || "";

      fieldErrors.general =
        nonField ||
        data.detail ||
        data.message ||
        "Не удалось войти. Попробуйте ещё раз.";

      setErrors(fieldErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthOverlay onClose={onClose}>
      <div
        className="
          relative bg-white rounded-[20px] flex flex-col items-center
          w-[100%] max-w-[360px] p-6
          sm:max-w-[400px]
          md:max-w-[440px] md:p-8
          lg:w-[482px] lg:p-[70px]
        "
        role="dialog"
        aria-modal="true"
      >
        {/* Logo block */}
        <div
          className="
            absolute flex items-center justify-center overflow-hidden rounded-[16px]
            w-12 h-12 -top-6
            sm:w-14 sm:h-14
            lg:w-16 lg:h-16 lg:-top-[5%]
          "
          aria-hidden="true"
        >
          <AuthLogoIcon/>
        </div>

        {/* Header */}
        <div
          className="
            mt-8 flex flex-col gap-3 items-stretch w-full
            lg:w-[358px] lg:h-[78px] lg:mt-6
          "
        >
          <h1
            className="
              m-0 font-bold text-center tracking-[-0.02em] text-[#01051F]
              text-[22px] leading-[30px]
              sm:text-[24px] sm:leading-[32px]
              lg:text-[28px] lg:leading-[38px]
            "
          >
            Войти в учётную запись
          </h1>
          <p
            className="
              m-0 font-medium text-center text-[#5C607C]
              text-[14px] leading-[22px]
              sm:text-[15px] sm:leading-[24px]
              lg:text-[18px] lg:leading-[28px]
            "
          >
            Ваш старт с нашим продуктом
          </p>
        </div>

        {/* Form */}
        <form
          className="
            mt-6 flex flex-col gap-2 w-full
            lg:w-[358px] lg:h-[226px]
          "
          onSubmit={handleSubmit}
        >
          {/* Логин */}
          <div className="flex flex-col gap-1 mb-2 lg:h-[100px]">
            <label
              className="
                inline-block h-6 font-medium text-[#333F51]
                text-[14px] leading-6
                sm:text-[15px]
                lg:text-[16px]
              "
            >
              Логин
            </label>
            <div
              className="
                box-border flex items-center gap-2 bg-white border border-[rgba(4,14,68,0.1)]
                shadow-sm rounded-[10px]
                w-full h-[44px] py-[10px] px-[14px]
                lg:w-[358px] lg:h-[46px]
              "
            >
              <input
                className="
                  w-full h-6 border-0 outline-none bg-transparent text-[#5C607C]
                  text-[15px] leading-6
                  sm:text-[16px]
                "
                type="text"
                placeholder="Email или телефон"
                value={form.login}
                onChange={handleChange("login")}
              />
            </div>
            {errors.login && (
              <p
                className="
                  mt-1 text-[#D30B0B]
                  text-[11px] leading-[18px]
                  sm:text-[12px]
                "
              >
                {errors.login}
              </p>
            )}
          </div>

          {/* Пароль */}
          <div className="flex flex-col gap-1 mb-2 lg:h-[100px]">
            <label
              className="
                inline-block h-6 font-medium text-[#333F51]
                text-[14px] leading-6
                sm:text-[15px]
                lg:text-[16px]
              "
            >
              Пароль
            </label>
            <div
              className="
                box-border flex items-center gap-2 bg-white border border-[rgba(4,14,68,0.1)]
                shadow-sm rounded-[10px]
                w-full h-[44px] py-[10px] px-[14px]
                lg:w-[358px] lg:h-[46px]
              "
            >
              <input
                className="
                  w-full h-6 border-0 outline-none bg-transparent text-[#5C607C]
                  text-[15px] leading-6
                  sm:text-[16px]
                "
                type="password"
                placeholder="Пароль"
                value={form.password}
                onChange={handleChange("password")}
              />
            </div>

            <div
              className="
                flex justify-between items-start mt-[6px]
                w-full
                lg:w-[358px]
              "
            >
              {errors.password || errors.general ? (
                <p
                  className="
                    text-[#D30B0B]
                    text-[11px] leading-[18px]
                    sm:text-[12px]
                  "
                >
                  {errors.password || errors.general}
                </p>
              ) : (
                <span/>
              )}

              <TextButton
                onClick={onForgot}
                className="
                  text-[11px]
                  sm:text-[12px]
                "
              >
                Забыли пароль?
              </TextButton>
            </div>
          </div>

          {/* Скрытая submit-кнопка для Enter */}
          <button type="submit" className="hidden" aria-hidden="true"></button>
        </form>

        {/* Buttons */}
        <div
          className="
            mt-6 flex flex-col gap-4 items-stretch w-full
            lg:w-[358px] lg:h-[116px] lg:gap-5
          "
        >
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full max-w-[358px]"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <LoadingOutlined style={{fontSize: 20, color: "white"}} spin/>
                <span>Войти</span>
              </div>
            ) : (
              "Войти"
            )}
          </PrimaryButton>

          <SecondaryButton
            disabled
            className="w-full max-w-[358px]"
          >
            <div className="flex items-center gap-[10px]">
              <div
                className="w-6 h-6 rounded-full bg-[linear-gradient(203.2deg,#37AEE2_21.67%,#1E96C8_70%)]"
                aria-hidden="true"
              />
              <span
                className="
                  font-bold text-[#5C607C]
                  text-[14px]
                  sm:text-[16px]
                "
              >
                Войти с помощью Telegram
              </span>
            </div>
          </SecondaryButton>
        </div>

        {/* Footer label */}
        <p
          className="
            mt-6 text-center font-medium text-[#333F51]
            w-full
            text-[11px] leading-[18px]
            sm:text-[12px]
            lg:w-[358px] lg:h-[18px]
          "
        >
          У вас нет учетной записи?{" "}
          <TextButton onClick={onSwitch}>Зарегистрируйтесь</TextButton>
        </p>
      </div>
    </AuthOverlay>
  );
}